import { refreshOwnedSession } from '../api/refresh-owned-session';
import { useCallback, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@shared/api/supabaseClient';
import { authContextApi } from '@shared/api/auth-context';
import { doctorAccountControlApi } from '@shared/api/doctor-account-control';
import { ApiClientError } from '@shared/api/api-client';
import type { UserProfile, V2AuthContext } from '@shared/types/auth';
import type { DoctorAccountControlRequest } from '@jema-manager/shared';
import { askBeforeSignOut } from '@shared/lib/beforeSignOut';
import { logger } from '@shared/lib/logger';
import { queryClient } from '@shared/lib/react-query';
import { queryPersister } from '@shared/lib/react-query-persist';
import { synchronizeClientStateWithUser } from '@shared/lib/session-isolation';
import { getMagicLinkRedirectUrl, isMobileApp } from '@shared/lib/webview-detection';
import { useAuthStore } from '@shared/stores/auth-store';
import {
  clearDoctorControlStorage,
  useDoctorControlStore,
} from '@shared/stores/doctor-control-store';
import {
  buildUserProfileFromV2AuthContext,
  hasAnyCapability as authContextHasAnyCapability,
  hasCapability as authContextHasCapability,
} from '@shared/lib/v2-auth';

const LEGACY_AUTH_STATE_KEYS = ['jema_auth_state', 'jema_auth_store_v1'];
const userProfileCache = new Map<
  string,
  { context: V2AuthContext; profile: UserProfile; timestamp: number }
>();
const CACHE_DURATION = 10 * 60 * 1000;

let updateInProgress = false;
let hasInitializedAuth = false;
let lastSessionUserId: string | null = null;
let authStateDebounce: ReturnType<typeof setTimeout> | null = null;
type PendingAuthUpdate = {
  session: Session | null;
  skipProfileCheck: boolean;
  waiters: Array<{
    resolve: () => void;
    reject: (error: unknown) => void;
  }>;
};
let pendingAuthUpdate: PendingAuthUpdate | null = null;
let authRetryTimer: ReturnType<typeof setTimeout> | null = null;
let authRetryAttempts = 0;

const MAX_AUTH_RETRY_ATTEMPTS = 3;
const AUTH_RETRY_DELAYS_MS = [2000, 5000, 10000];

const getEmailLogContext = (email: string) => {
  const [, domain = 'unknown'] = email.split('@');
  return { emailDomain: domain.toLowerCase() };
};

const isAuthFailure = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const normalizedMessage = message.toLowerCase();
  const status = Number((error as any)?.status ?? 0);
  const code = String((error as any)?.code ?? '').toUpperCase();
  const name = String((error as any)?.name ?? '');

  return (
    status === 401 ||
    code === 'AUTHENTICATION_REQUIRED' ||
    code === 'UNAUTHORIZED' ||
    normalizedMessage.includes('invalid or expired token') ||
    normalizedMessage.includes('authentication required') ||
    (name === 'ApiClientError' && status === 401)
  );
};

const isRetryableAuthProfileError = (error: unknown) => {
  if (error instanceof ApiClientError) {
    return error.retryable && !isAuthFailure(error);
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('timeout') || message.includes('network');
  }

  return false;
};

const clearAuthRetry = () => {
  if (authRetryTimer) {
    clearTimeout(authRetryTimer);
    authRetryTimer = null;
  }
  authRetryAttempts = 0;
};

const getCurrentSessionUserId = async (): Promise<string | null> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user?.id ?? null;
  } catch (error) {
    logger.warn('Failed to read current session user id', { error });
    return null;
  }
};

const clearLegacyAuthState = () => {
  LEGACY_AUTH_STATE_KEYS.forEach(storageKey => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore localStorage write failures.
    }
  });
};

const updateStoreSnapshot = (state: {
  user: UserProfile | null;
  authContext: V2AuthContext | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}) => {
  useAuthStore.getState().setAuthSnapshot(state);
  clearLegacyAuthState();
};

const clearStoreSnapshot = () => {
  useAuthStore.getState().clearAuth();
  useDoctorControlStore.getState().clearControl();
  clearLegacyAuthState();
};

const fetchUserProfile = async (
  session: Session
): Promise<{ authContext: V2AuthContext; userProfile: UserProfile }> => {
  const userId = session.user.id;
  const cached = userProfileCache.get(userId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return {
      authContext: cached.context,
      userProfile: cached.profile,
    };
  }

  try {
    const authContext = await authContextApi.getCurrentContext(session.access_token, {
      skipDoctorControlHeader: true,
    });
    const userProfile = buildUserProfileFromV2AuthContext(session, authContext);

    userProfileCache.set(userId, {
      context: authContext,
      profile: userProfile,
      timestamp: Date.now(),
    });
    return {
      authContext,
      userProfile,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const errorStatus = (error as any)?.status;
    const errorCode = (error as any)?.code;
    const errorName = (error as any)?.name;

    const isSuspended =
      message.includes('suspended') ||
      message.includes('SUSPENDED') ||
      message.includes('Your account has been suspended') ||
      errorStatus === 403 ||
      errorCode === 'FORBIDDEN' ||
      (errorName === 'ApiClientError' && errorStatus === 403);

    if (isAuthFailure(error)) {
      logger.warn('Backend auth context rejected session; recovery handler will clear it', {
        userId,
        message,
        errorStatus,
        errorCode,
      });
    } else if (isRetryableAuthProfileError(error)) {
      logger.warn(
        'Backend auth context request failed with retryable error; recovery handler will retry',
        {
          userId,
          message,
          errorStatus,
          errorCode,
        }
      );
    } else if (!isSuspended) {
      logger.error('Backend API call failed', { userId, error });
    }

    if (isSuspended) {
      const suspensionMessage =
        'Your account has been suspended. Please contact support for assistance.';
      logger.warn('User account suspended', { userId, message });
      window.location.href = `/auth/error?message=${encodeURIComponent(suspensionMessage)}&code=SUSPENDED`;
      return {
        authContext: {
          userId,
          authUserId: userId,
          userType: 'patient',
          status: 'suspended',
          primaryEmail: 'suspended@example.com',
          profile: {
            displayName: 'Suspended User',
            firstName: 'Suspended',
            lastName: 'User',
            phone: null,
            locale: null,
            timezone: null,
          },
          activeOrganizationId: null,
          activeMembershipId: null,
          activeRoleTemplateCode: null,
          memberships: [],
          capabilities: [],
        },
        userProfile: {
          id: userId,
          role: 'patient',
          email: 'suspended@example.com',
          first_name: 'Suspended',
          last_name: 'User',
          onboarding_complete: true,
          organization_roles: [],
        },
      };
    }

    throw error;
  }
};

const clearControlScopedRuntimeState = async (reason: string) => {
  try {
    queryClient.clear();
  } catch (error) {
    logger.warn('Failed to clear query cache for doctor control transition', { reason, error });
  }

  try {
    await queryPersister.removeClient();
  } catch (error) {
    logger.warn('Failed to clear persisted query cache for doctor control transition', {
      reason,
      error,
    });
  }
};

const fetchDelegatedDoctorProfile = async (
  session: Session,
  activeRequest: NonNullable<ReturnType<typeof useDoctorControlStore.getState>['activeRequest']>
): Promise<{ authContext: V2AuthContext; userProfile: UserProfile }> => {
  const authContext = await authContextApi.getCurrentContext(session.access_token, {
    doctorControlRequestId: activeRequest.id,
  });
  const userProfile = buildUserProfileFromV2AuthContext(session, authContext);

  return { authContext, userProfile };
};

const updateAuthState = async (session: Session | null, skipProfileCheck = false) => {
  if (updateInProgress) {
    return new Promise<void>((resolve, reject) => {
      if (pendingAuthUpdate) {
        pendingAuthUpdate = {
          session,
          skipProfileCheck,
          waiters: [...pendingAuthUpdate.waiters, { resolve, reject }],
        };
        return;
      }

      pendingAuthUpdate = {
        session,
        skipProfileCheck,
        waiters: [{ resolve, reject }],
      };
    });
  }

  updateInProgress = true;

  try {
    if (session?.user && session.access_token) {
      const userId = session.user.id;
      const switchedUsers = Boolean(lastSessionUserId && lastSessionUserId !== userId);

      await synchronizeClientStateWithUser(userId);

      if (switchedUsers) {
        userProfileCache.clear();
      }

      if (skipProfileCheck && lastSessionUserId === userId) {
        const cached = userProfileCache.get(userId);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          clearAuthRetry();
          const activeDoctorControlRequest = useDoctorControlStore.getState().activeRequest;
          const canProjectDoctorControl =
            (cached.profile.role === 'admin' || cached.profile.role === 'super_admin') &&
            activeDoctorControlRequest?.status === 'active';
          if (canProjectDoctorControl) {
            try {
              const delegatedProfile = await fetchDelegatedDoctorProfile(
                session,
                activeDoctorControlRequest
              );
              useAuthStore.getState().setEffectiveAuthSnapshot({
                actorUser: cached.profile,
                actorAuthContext: cached.context,
                effectiveUser: delegatedProfile.userProfile,
                effectiveAuthContext: delegatedProfile.authContext,
                activeDoctorControlRequest,
                isLoading: false,
                isAuthenticated: true,
              });
            } catch (error) {
              logger.warn(
                'Clearing unavailable doctor control session during cached auth refresh',
                {
                  controlRequestId: activeDoctorControlRequest.id,
                  error,
                }
              );
              clearDoctorControlStorage();
              await clearControlScopedRuntimeState('doctor_control_unavailable');
              updateStoreSnapshot({
                user: cached.profile,
                authContext: cached.context,
                isLoading: false,
                isAuthenticated: true,
              });
            }
          } else {
            updateStoreSnapshot({
              user: cached.profile,
              authContext: cached.context,
              isLoading: false,
              isAuthenticated: true,
            });
          }
          return;
        }
      }

      lastSessionUserId = userId;

      try {
        const { authContext, userProfile } = await fetchUserProfile(session);
        const activeDoctorControlRequest = useDoctorControlStore.getState().activeRequest;
        const canProjectDoctorControl =
          (userProfile.role === 'admin' || userProfile.role === 'super_admin') &&
          activeDoctorControlRequest?.status === 'active';
        let delegatedProfile: { authContext: V2AuthContext; userProfile: UserProfile } | null =
          null;
        let delegatedControlRequest: DoctorAccountControlRequest | null = null;
        if (canProjectDoctorControl) {
          try {
            delegatedProfile = await fetchDelegatedDoctorProfile(
              session,
              activeDoctorControlRequest
            );
            delegatedControlRequest = activeDoctorControlRequest;
          } catch (error) {
            logger.warn('Clearing unavailable doctor control session during auth refresh', {
              controlRequestId: activeDoctorControlRequest.id,
              error,
            });
            clearDoctorControlStorage();
            await clearControlScopedRuntimeState('doctor_control_unavailable');
          }
        }
        clearAuthRetry();
        const currentSessionUserId = await getCurrentSessionUserId();
        if (currentSessionUserId && currentSessionUserId !== userId) {
          logger.warn('Ignoring stale auth profile response', {
            expectedUserId: userId,
            currentSessionUserId,
          });
          return;
        }

        if (delegatedProfile && delegatedControlRequest) {
          useAuthStore.getState().setEffectiveAuthSnapshot({
            actorUser: userProfile,
            actorAuthContext: authContext,
            effectiveUser: delegatedProfile.userProfile,
            effectiveAuthContext: delegatedProfile.authContext,
            activeDoctorControlRequest: delegatedControlRequest,
            isLoading: false,
            isAuthenticated: true,
          });
        } else {
          updateStoreSnapshot({
            user: userProfile,
            authContext,
            isLoading: false,
            isAuthenticated: true,
          });
        }

        const effectiveUser = delegatedProfile?.userProfile ?? userProfile;
        const effectiveAuthContext = delegatedProfile?.authContext ?? authContext;
        logger.setUser(effectiveUser.id, effectiveUser.email, effectiveUser.role);
        logger.setContext('auth', {
          role: effectiveUser.role,
          actorRole: userProfile.role,
          doctorControlRequestId: delegatedControlRequest?.id ?? null,
          organizationRoles: effectiveUser.organization_roles,
          activeOrganizationId: effectiveAuthContext.activeOrganizationId,
          capabilities: effectiveAuthContext.capabilities,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const errorStatus = (error as any)?.status;
        const errorCode = (error as any)?.code;
        const errorName = (error as any)?.name;

        const isSuspended =
          message.includes('suspended') ||
          message.includes('SUSPENDED') ||
          message.includes('Your account has been suspended') ||
          errorStatus === 403 ||
          errorCode === 'FORBIDDEN' ||
          (errorName === 'ApiClientError' && errorStatus === 403);

        if (isSuspended) {
          const suspensionMessage =
            'Your account has been suspended. Please contact support for assistance.';
          updateStoreSnapshot({
            user: null,
            authContext: null,
            isLoading: false,
            isAuthenticated: false,
          });
          window.location.href = `/auth/error?message=${encodeURIComponent(suspensionMessage)}&code=SUSPENDED`;
          return;
        }

        const currentSessionUserId = await getCurrentSessionUserId();
        if (currentSessionUserId && currentSessionUserId !== userId) {
          logger.warn('Skipping auth fallback for stale session error', {
            expectedUserId: userId,
            currentSessionUserId,
            message,
          });
          return;
        }

        if (isAuthFailure(error)) {
          clearAuthRetry();
          logger.warn('Auth context fetch failed; clearing invalid session', {
            userId,
            message,
            errorStatus,
            errorCode,
          });
          await synchronizeClientStateWithUser(null);
          userProfileCache.delete(userId);
          lastSessionUserId = null;
          clearStoreSnapshot();
          logger.clearUser();
          await supabase.auth.signOut({ scope: 'local' } as any);
          return;
        }

        const cached = userProfileCache.get(userId);
        if (cached) {
          clearAuthRetry();
          updateStoreSnapshot({
            user: cached.profile,
            authContext: cached.context,
            isLoading: false,
            isAuthenticated: true,
          });
        } else if (isRetryableAuthProfileError(error)) {
          const canRetry = authRetryAttempts < MAX_AUTH_RETRY_ATTEMPTS;
          const retryDelay =
            AUTH_RETRY_DELAYS_MS[Math.min(authRetryAttempts, AUTH_RETRY_DELAYS_MS.length - 1)];
          const persistedAuth = useAuthStore.getState();
          const canUsePersistedSnapshot =
            persistedAuth.user?.id === userId &&
            persistedAuth.authContext?.authUserId === userId &&
            persistedAuth.isAuthenticated;

          if (canRetry) {
            authRetryAttempts += 1;
            if (authRetryTimer) {
              clearTimeout(authRetryTimer);
            }
            authRetryTimer = setTimeout(() => {
              authRetryTimer = null;
              void updateAuthState(session, false);
            }, retryDelay);
          }

          if (canUsePersistedSnapshot && persistedAuth.user && persistedAuth.authContext) {
            userProfileCache.set(userId, {
              context: persistedAuth.authContext,
              profile: persistedAuth.user,
              timestamp: Date.now(),
            });
            updateStoreSnapshot({
              user: persistedAuth.user,
              authContext: persistedAuth.authContext,
              isLoading: false,
              isAuthenticated: true,
            });
            logger.warn(
              'Auth profile fetch failed with retryable error; using persisted auth snapshot',
              {
                userId,
                message,
                retryAttempt: authRetryAttempts,
                retryScheduled: canRetry,
                retryDelay,
              }
            );
            return;
          }

          logger.warn('Auth profile fetch failed with retryable error; preserving session', {
            userId,
            message,
            retryAttempt: authRetryAttempts,
            retryScheduled: canRetry,
            retryDelay,
          });
          useAuthStore.getState().setLoading(canRetry);
        } else {
          clearAuthRetry();
          updateStoreSnapshot({
            user: null,
            authContext: null,
            isLoading: false,
            isAuthenticated: false,
          });
          logger.error('Auth profile fetch failed with no cache; signing out session', {
            userId,
            message,
            error,
          });
          await supabase.auth.signOut();
        }
      }
    } else {
      clearAuthRetry();
      const currentSessionUserId = await getCurrentSessionUserId();
      if (currentSessionUserId) {
        logger.warn(
          'Received null auth session while another session is still active; skipping clear',
          {
            currentSessionUserId,
          }
        );
        return;
      }

      await synchronizeClientStateWithUser(null);
      lastSessionUserId = null;
      userProfileCache.clear();
      clearStoreSnapshot();
      logger.clearUser();
    }
  } finally {
    updateInProgress = false;

    if (pendingAuthUpdate) {
      const next = pendingAuthUpdate;
      pendingAuthUpdate = null;
      try {
        await updateAuthState(next.session, next.skipProfileCheck);
        next.waiters.forEach(({ resolve }) => resolve());
      } catch (error) {
        next.waiters.forEach(({ reject }) => reject(error));
      }
    }
  }
};

const bootstrapAuthState = async () => {
  try {
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Initial session check timeout')), 10000);
    });

    const {
      data: { session },
    } = await Promise.race([sessionPromise, timeoutPromise]);

    await updateAuthState(session, false);
  } catch (error) {
    logger.warn('Initial session check failed', { error });
    useAuthStore.getState().setLoading(false);
  }
};

const initializeAuth = () => {
  if (hasInitializedAuth) {
    if (useAuthStore.getState().isLoading) {
      void bootstrapAuthState();
    }
    return;
  }

  hasInitializedAuth = true;
  useAuthStore.getState().validateSnapshot();
  useAuthStore.getState().beginSessionRevalidation();

  void bootstrapAuthState();

  supabase.auth.onAuthStateChange(async (_event, session) => {
    if (authStateDebounce) {
      clearTimeout(authStateDebounce);
    }

    authStateDebounce = setTimeout(() => {
      void updateAuthState(session, true);
    }, 150);
  });

  setTimeout(() => {
    const { isLoading } = useAuthStore.getState();
    if (isLoading) {
      useAuthStore.getState().setLoading(false);
    }
  }, 15000);
};

export const useAuth = () => {
  const user = useAuthStore(state => state.user);
  const authContext = useAuthStore(state => state.authContext);
  const actorUser = useAuthStore(state => state.actorUser);
  const actorAuthContext = useAuthStore(state => state.actorAuthContext);
  const effectiveUser = useAuthStore(state => state.effectiveUser);
  const effectiveAuthContext = useAuthStore(state => state.effectiveAuthContext);
  const isDoctorControlMode = useAuthStore(state => state.isDoctorControlMode);
  const activeDoctorControlRequest = useAuthStore(state => state.activeDoctorControlRequest);
  const capabilities = useAuthStore(state => state.capabilities);
  const isLoading = useAuthStore(state => state.isLoading);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  useEffect(() => {
    initializeAuth();
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    logger.info('Attempting password sign-in', getEmailLogContext(email));
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      logger.error('Password sign-in failed', {
        ...getEmailLogContext(email),
        error,
      });
      throw error;
    }

    if (data.session) {
      await updateAuthState(data.session, false);
      logger.info('Password sign-in succeeded', {
        ...getEmailLogContext(email),
        userId: data.session.user.id,
      });
    } else {
      logger.warn('Password sign-in returned without a session', getEmailLogContext(email));
    }

    const authState = useAuthStore.getState();
    if (!authState.isAuthenticated || !authState.user) {
      logger.error('Password sign-in completed without authenticated auth state', {
        ...getEmailLogContext(email),
        hasSession: Boolean(data.session),
        isAuthenticated: authState.isAuthenticated,
        hasUser: Boolean(authState.user),
      });
      throw new Error('Unable to complete sign in. Please try again.');
    }

    return data;
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    const redirectUrl = getMagicLinkRedirectUrl({ appRequested: isMobileApp() });

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      logger.error('Magic link sign-in failed', {
        ...getEmailLogContext(email),
        error,
      });
      throw error;
    }

    logger.info('Magic link sign-in initiated', getEmailLogContext(email));
    return data;
  }, []);

  const sendPatientOtp = useCallback(async (email: string) => {
    logger.info('Sending patient OTP', getEmailLogContext(email));
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      },
    });

    if (error) {
      logger.error('Sending patient OTP failed', {
        ...getEmailLogContext(email),
        error,
      });
      throw error;
    }

    logger.info('Patient OTP sent', getEmailLogContext(email));
  }, []);

  const verifyPatientOtp = useCallback(async (email: string, token: string) => {
    logger.info('Verifying patient OTP', getEmailLogContext(email));
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error) {
      logger.error('Patient OTP verification failed', {
        ...getEmailLogContext(email),
        error,
      });
      throw error;
    }

    if (data.session) {
      await updateAuthState(data.session, false);
      logger.info('Patient OTP verification succeeded', {
        ...getEmailLogContext(email),
        userId: data.session.user.id,
      });
    } else {
      logger.warn('Patient OTP verification returned without a session', getEmailLogContext(email));
    }

    const authState = useAuthStore.getState();
    if (!authState.isAuthenticated || !authState.user) {
      logger.error('OTP verification completed without authenticated auth state', {
        ...getEmailLogContext(email),
        hasSession: Boolean(data.session),
        isAuthenticated: authState.isAuthenticated,
        hasUser: Boolean(authState.user),
      });
      throw new Error('Unable to complete sign in. Please try again.');
    }

    return data;
  }, []);

  const signOut = useCallback(async () => {
    // A live phone call asks first (calls v2); a "no" throws and nothing is signed out.
    await askBeforeSignOut();

    const activeDoctorControl = useDoctorControlStore.getState().activeRequest;
    if (activeDoctorControl?.status === 'active') {
      try {
        await doctorAccountControlApi.end(activeDoctorControl.id);
      } catch (error) {
        logger.warn('Failed to end active doctor control during sign out', { error });
      } finally {
        useDoctorControlStore.getState().clearControl();
      }
    }

    await synchronizeClientStateWithUser(null);
    userProfileCache.clear();
    lastSessionUserId = null;
    clearStoreSnapshot();
    logger.clearUser();

    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.error('Sign out failed', { error });
      throw error;
    }

    logger.info('Sign out completed');
  }, []);

  const refreshSession = useCallback(async () => {
    const { data, error } = await refreshOwnedSession();
    if (error) {
      throw error;
    }

    if (data.session) {
      await updateAuthState(data.session, false);
    }

    return data;
  }, []);

  const refetchProfile = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) return;

    userProfileCache.delete(session.user.id);
    await updateAuthState(session, false);
  }, []);

  const switchOrganizationContext = useCallback(async (organizationId: string | null) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const authContext = await authContextApi.switchContext(organizationId);
    const userProfile = buildUserProfileFromV2AuthContext(session, authContext);
    userProfileCache.set(session.user.id, {
      context: authContext,
      profile: userProfile,
      timestamp: Date.now(),
    });
    updateStoreSnapshot({
      user: userProfile,
      authContext,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    (window as any).jemaAuth = {
      currentState: { user, isLoading, isAuthenticated },
      forceRefresh: async () => {
        hasInitializedAuth = false;
        initializeAuth();
      },
      clearCache: () => {
        userProfileCache.clear();
        clearStoreSnapshot();
        lastSessionUserId = null;
      },
      getSessionInfo: async () => {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        return {
          hasSession: !!session,
          userId: session?.user?.id,
          email: session?.user?.email,
          expiresAt: session?.expires_at,
          accessToken: session?.access_token ? 'present' : 'missing',
        };
      },
    };
  }, [user, isLoading, isAuthenticated]);

  return {
    user,
    authContext,
    actorUser,
    actorAuthContext,
    effectiveUser,
    effectiveAuthContext,
    isDoctorControlMode,
    activeDoctorControlRequest,
    capabilities,
    isLoading,
    isAuthenticated,
    hasCapability: (capability: string) => authContextHasCapability(capabilities, capability),
    hasAnyCapability: (requiredCapabilities: string[]) =>
      authContextHasAnyCapability(capabilities, requiredCapabilities),
    signInWithPassword,
    signInWithMagicLink,
    sendPatientOtp,
    verifyPatientOtp,
    signOut,
    refreshSession,
    refetchProfile,
    switchOrganizationContext,
  };
};
